import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildCatalogPath, buildTopbarMenuStructure, buildValidSelections } from "../../utils/topbarUtils";
import { getMenuGroupTitle } from "../../utils/familyMenu";
import { redirectToSSOLogin } from "../../../utils/ssoRedirect";

const useTopbarMenu = ({
  collections = [],
  catalogMenu = [],
  navItems = [],
  isMobileView,
  isCheckoutFlow,
  navigate,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(collections[0] || "Todos");
  const [selectedSubcollection, setSelectedSubcollection] = useState("");
  const [activeDesktopGroup, setActiveDesktopGroup] = useState("");
  const [activeDesktopCollection, setActiveDesktopCollection] = useState(catalogMenu[0]?.name || navItems[0] || "");
  const closeTimerRef = useRef(null);

  const menuStructure = useMemo(() => buildTopbarMenuStructure(catalogMenu, navItems), [catalogMenu, navItems]);
  const validSelections = useMemo(() => buildValidSelections(collections, menuStructure), [collections, menuStructure]);
  const shouldShowDesktopCollections = true && !isMobileView;

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // overlay body class
  useEffect(() => {
    const shouldOpenDesktopOverlay = isDesktopMenuOpen && !isMobileView;
    document.body.classList.toggle("landing-desktop-menu-open", shouldOpenDesktopOverlay);
    return () => {
      document.body.classList.remove("landing-desktop-menu-open");
    };
  }, [isDesktopMenuOpen, isMobileView]);

  // reset on checkout
  useEffect(() => {
    if (isCheckoutFlow) {
      setIsDesktopMenuOpen(false);
      closeMobileMenu();
    }
  }, [isCheckoutFlow, closeMobileMenu]);

  // validity of selectedCollection
  useEffect(() => {
    if (!validSelections.includes(selectedCollection)) {
      setSelectedCollection(validSelections[0] || "Todos");
      setSelectedSubcollection("");
    }
  }, [validSelections, selectedCollection]);

  // close mobile drawer when switching to desktop
  useEffect(() => {
    if (!isMobileView) {
      closeMobileMenu();
    }
  }, [isMobileView, closeMobileMenu]);

  // clear pending desktop close timer on unmount
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleAllProductsNavigation = useCallback(({ closeDesktopMenu = false, closeMobileDrawer = false } = {}) => {
    setSelectedCollection("Todos");
    setSelectedSubcollection("");
    if (closeDesktopMenu) setIsDesktopMenuOpen(false);
    if (closeMobileDrawer) closeMobileMenu();
    navigate("/catalogo");
  }, [closeMobileMenu, navigate]);

  const handleCatalogNavigation = useCallback(({
    collection = "",
    subcollection = "",
    familyId = 0,
    closeDesktopMenu = false,
    closeMobileDrawer = false,
  }) => {
    setSelectedCollection(collection || "Todos");
    setSelectedSubcollection(subcollection || "");
    if (closeDesktopMenu) setIsDesktopMenuOpen(false);
    if (closeMobileDrawer) closeMobileMenu();
    navigate(buildCatalogPath({ collection, subcollection, familyId, menuStructure }));
  }, [closeMobileMenu, menuStructure, navigate]);

  const handleOpenLogin = useCallback(() => {
    redirectToSSOLogin();
    closeMobileMenu();
  }, [closeMobileMenu]);

  const cancelDesktopMenuClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const scheduleDesktopMenuClose = useCallback(() => {
    cancelDesktopMenuClose();
    closeTimerRef.current = setTimeout(() => setIsDesktopMenuOpen(false), 180);
  }, [cancelDesktopMenuClose]);

  const handleDesktopCollectionHover = useCallback((item) => {
    if (!shouldShowDesktopCollections) return;
    setActiveDesktopCollection(item.name);
    const hasGroups = Array.isArray(item?.groups) && item.groups.length > 0;
    if (hasGroups) {
      setActiveDesktopGroup(getMenuGroupTitle(item.groups[0]));
    }
  }, [shouldShowDesktopCollections]);

  const handleDesktopMenuToggle = useCallback(() => {
    if (!shouldShowDesktopCollections || !menuStructure.length) return;
    if (!activeDesktopCollection) {
      setActiveDesktopCollection(menuStructure[0].name);
    }
    setIsDesktopMenuOpen((prev) => !prev);
  }, [shouldShowDesktopCollections, menuStructure, activeDesktopCollection]);

  const handleDesktopGroupHover = useCallback((group) => {
    setActiveDesktopGroup(getMenuGroupTitle(group));
  }, []);

  return {
    menuStructure,
    shouldShowCollections: true,
    shouldShowDesktopCollections,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isDesktopMenuOpen,
    selectedCollection,
    selectedSubcollection,
    activeDesktopCollection,
    activeDesktopGroup,
    closeMobileMenu,
    handleAllProductsNavigation,
    handleCatalogNavigation,
    handleOpenLogin,
    cancelDesktopMenuClose,
    scheduleDesktopMenuClose,
    handleDesktopCollectionHover,
    handleDesktopMenuToggle,
    handleDesktopGroupHover,
    setSelectedCollection,
    setSelectedSubcollection,
    setIsDesktopMenuOpen,
  };
};

export default useTopbarMenu;